import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import {
  AuthSessionGateway,
  ShellConfigService,
  TokenStorageService,
} from 'continity-shared-shell';
import {
  B2cAuthProvider,
  continueLogoutNavigation,
  LogoutNavigationResponse,
} from 'continity-shared-auth';

/**
 * Phoenix auth-session gateway. Login-initiation now runs the REAL Azure AD B2C
 * authorization-code + PKCE flow directly (PBI 1466) — straight to the hosted B2C sign-in
 * page, no detour through the main Continity portal's `/login`. The flow is backend-brokered
 * (see {@link B2cAuthProvider}); `redirect_uri` is THIS Phoenix origin's `/auth-callback`.
 *
 *  - {@link initiateLogin} delegates to {@link B2cAuthProvider.initiateLogin}. If that
 *    login-url fetch fails (misconfig / outage), Phoenix returns to its own manual login
 *    page. It must never depend on a main-portal refresh cookie: cookies are host-only.
 *  - {@link logout} clears the in-memory bearer and calls `/Auth/logout-spa` to delete the
 *    Phoenix-host cookie + the paired main-portal cookie, landing back on THIS Phoenix
 *    portal's landing page. B2C sessions additionally terminate at the identity provider;
 *    local DevAuth sessions skip that final external hop. The B2C path needs the Phoenix origin
 *    BOTH in the server redirect whitelist
 *    (`AzureB2C:AllowedRedirectUris` — :4300 for local/dev/test) AND on the B2C app
 *    registration itself (reply URL).
 *
 * Wired via the AUTH_SESSION_GATEWAY token in app.config; without it the shell's
 * AuthService.initiateLogin/logout are documented no-ops.
 */
@Injectable()
export class PhoenixAuthGateway implements AuthSessionGateway {
  constructor(
    private http: HttpClient,
    private config: ShellConfigService,
    private tokenStorage: TokenStorageService,
    private translate: TranslateService,
    private injector: Injector
  ) {}

  initiateLogin(returnUrl?: string): void {
    const target = returnUrl ?? '/board';
    // Resolve B2cAuthProvider LAZILY (not via the constructor): it injects AuthService, and
    // AuthService eagerly injects THIS gateway (AUTH_SESSION_GATEWAY) at construction — an
    // eager constructor dep here would close a DI cycle (NG0200). Resolving at call time,
    // well after construction, breaks it.
    const b2c = this.injector.get(B2cAuthProvider);
    // Direct B2C (PKCE) — the browser leaves for the hosted B2C page on success.
    b2c
      .initiateLogin(target, { uiLocales: this.translate.currentLang || 'en' })
      .catch((err) => {
        // Remove the auto-start marker before rendering the manual page; otherwise a
        // backend/configuration failure would create an immediate redirect loop.
        console.warn(
          '[phoenix] direct B2C login failed, falling back to manual Phoenix login',
          err
        );
        window.location.replace(`/login?returnUrl=${encodeURIComponent(target)}`);
      });
  }

  logout(): void {
    // Land back on THIS Phoenix portal's landing page after sign-out (NOT the Continity
    // portal). See the class doc: exact for the dev-login path; the B2C path additionally
    // needs the Phoenix origin whitelisted server-side + on the B2C app registration (1466).
    const postLogout = window.location.origin;
    this.tokenStorage.clearToken();
    // logout-spa deletes this host's cookie and returns the configured peer front-channel
    // URL. Top-level navigation lets that host delete its own host-only cookie before the
    // optional B2C sign-out. withCredentials carries the current host's refresh cookie.
    const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');
    const requestUrl = `${this.config.apiUrl}/Auth/logout-spa`;
    this.http
      .post<LogoutNavigationResponse>(requestUrl, {}, {
        params: {
          postLogoutRedirectUri: postLogout,
        },
        withCredentials: true,
        headers,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404 || error.status === 405) {
            return this.http.get<LogoutNavigationResponse>(requestUrl, {
              params: { postLogoutRedirectUri: postLogout },
              withCredentials: true,
              headers,
            });
          }
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (res) => continueLogoutNavigation(res, postLogout),
        error: () => window.location.replace(postLogout),
      });
  }
}
